import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Drawer, Flex, Stack } from "@mantine/core";

import { useMissingServicesFilterProviders } from "@/billing/hooks/useMissingServicesFilterProviders";

import {
  EMPTY_MISSING_SERVICES_FILTERS,
  MISSING_SERVICES_ENCOUNTER_FILTER_SECTION,
  MISSING_SERVICES_STATUS_FILTER_SECTION,
} from "./constants/missing-services";
import MissingServicesFilterDrawerSection from "./MissingServicesFilterDrawerSection";
import MissingServicesFilterProviderSection from "./MissingServicesFilterProviderSection";
import { MissingServicesFilterDrawerProps } from "./types/missing-services";
import { isMissingServiceStatus } from "./utils/missing-services-filter-params";

export default function MissingServicesFilterDrawer({
  opened,
  onClose,
  committedFilters,
  onApply,
}: MissingServicesFilterDrawerProps) {
  const { practiceId } = useParams<{ practiceId: string }>();
  const { providers, isLoading: isLoadingProviders } = useMissingServicesFilterProviders(
    practiceId,
    opened,
  );
  const [draftFilters, setDraftFilters] = useState(committedFilters);

  useEffect(() => {
    if (!opened) return;
    setDraftFilters(committedFilters);
  }, [opened, committedFilters]);

  const toggleEncounterType = (key: string) => {
    setDraftFilters((current) => ({
      ...current,
      encounterTypeKeys: toggleSelectedKey(current.encounterTypeKeys, key),
    }));
  };

  // The By Status section only renders the two known statuses. Any other key would be a bug, not
  // something a user could click, so ignore it instead of widening `statuses` to accept it.
  const toggleStatus = (key: string) => {
    if (!isMissingServiceStatus(key)) return;
    setDraftFilters((current) => ({
      ...current,
      statuses: toggleSelectedKey(current.statuses, key),
    }));
  };

  // The multi-select reports its whole selection, so this replaces `providerIds` rather than
  // toggling one id the way the two checkbox sections do.
  const changeProviders = (providerIds: string[]) => {
    setDraftFilters((current) => ({ ...current, providerIds }));
  };

  const handleReset = () => {
    setDraftFilters(EMPTY_MISSING_SERVICES_FILTERS);
  };

  const handleApply = () => {
    onApply(draftFilters);
    onClose();
  };

  return (
    <Drawer.Root
      opened={opened}
      position="right"
      onClose={onClose}
      data-testid="missing-services-filter-drawer"
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header bg="gray.0">
          <Flex gap={12} align="center">
            <Drawer.CloseButton ms="unset" data-testid="missing-services-filter-drawer-close" />
            <Drawer.Title c="dark.9" variant="bold">
              Filter Services
            </Drawer.Title>
          </Flex>
          <Flex gap={12}>
            <Button
              variant="outline"
              onClick={handleReset}
              data-testid="missing-services-filter-reset-button"
            >
              Reset
            </Button>
            <Button onClick={handleApply} data-testid="missing-services-filter-apply-button">
              Apply
            </Button>
          </Flex>
        </Drawer.Header>
        <Drawer.Body my="md">
          <Stack gap="lg">
            <MissingServicesFilterDrawerSection
              onToggle={toggleEncounterType}
              selectedKeys={draftFilters.encounterTypeKeys}
              section={MISSING_SERVICES_ENCOUNTER_FILTER_SECTION}
            />
            <MissingServicesFilterDrawerSection
              onToggle={toggleStatus}
              selectedKeys={draftFilters.statuses}
              section={MISSING_SERVICES_STATUS_FILTER_SECTION}
            />
            <MissingServicesFilterProviderSection
              providers={providers}
              isLoading={isLoadingProviders}
              onSelectionChange={changeProviders}
              selectedIds={draftFilters.providerIds}
            />
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}

/**
 * Adds an option key to a draft selection, or removes it when it is already selected.
 * @param selectedKeys the section's current draft selection
 * @param key the clicked option's key
 * @returns a new array with `key` toggled
 */
function toggleSelectedKey<Key extends string>(selectedKeys: Key[], key: Key): Key[] {
  if (selectedKeys.includes(key)) return selectedKeys.filter((selected) => selected !== key);
  return [...selectedKeys, key];
}
